import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, Chip, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, Button, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m - 1]} ${+d}, ${y}`;
}

function placeBadge(place) {
  let color = '#888';
  if (place === 1) color = '#FFD700'; // gold
  else if (place === 2) color = '#C0C0C0'; // silver
  else if (place === 3) color = '#CD7F32'; // bronze
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: '50%',
      backgroundColor: place <= 3 ? color : '#f0f2f5',
      color: place <= 3 ? '#0D1E35' : '#666',
      fontWeight: 700, fontSize: '0.9rem',
    }}>
      {place}
    </Box>
  );
}

export default function TournamentResults() {
  const { tid } = useParams();
  const [results, setResults] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDivision, setActiveDivision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [resSnap, tSnap] = await Promise.all([
          getDoc(doc(db, 'tournamentResults', tid)),
          getDoc(doc(db, 'tournaments', tid)),
        ]);
        if (cancelled) return;
        if (!tSnap.exists()) {
          setError('Tournament not found.');
          setLoading(false);
          return;
        }
        setTournament({ id: tSnap.id, ...tSnap.data() });
        if (resSnap.exists()) {
          setResults({ id: resSnap.id, ...resSnap.data() });
        } else {
          setResults(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tid]);

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button component={Link} to="/tournaments" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Tournaments
        </Button>
      </Container>
    );
  }

  const divisionNames = results ? Object.keys(results.divisions || {}) : [];

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#C9A84C' }} />
                {tournament?.name || 'Tournament Results'}
              </Typography>
              {tournament?.date && (
                <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                  {fmtDate(tournament.date)}
                  {tournament.location ? ` · ${tournament.location}` : ''}
                </Typography>
              )}
            </Box>
            <Button component={Link} to="/tournaments" variant="outlined" color="inherit" startIcon={<ArrowBackIcon />}
                    sx={{ borderColor: 'rgba(255,255,255,0.4)' }}>
              All Tournaments
            </Button>
          </Box>
        </Container>
      </Box>

      <Container sx={{ pb: 6 }}>
        {!results ? (
          <Alert severity="info">
            Results have not been imported for this tournament yet.
          </Alert>
        ) : divisionNames.length === 0 ? (
          <Alert severity="info">No divisions found in the imported results.</Alert>
        ) : (
          <>
            <Tabs value={activeDivision} onChange={(_, v) => setActiveDivision(v)}
                  variant="scrollable" scrollButtons="auto"
                  sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              {divisionNames.map((name, i) => (
                <Tab key={name} label={name} />
              ))}
            </Tabs>

            {divisionNames.map((name, i) => {
              if (i !== activeDivision) return null;
              const div = results.divisions[name];
              const standings = div.standings || [];
              return (
                <Card key={name} elevation={2} sx={{ borderRadius: 3 }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">{name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {div.status && (
                        <Chip label={div.status} size="small"
                              color={div.status.toLowerCase() === 'complete' ? 'success' : 'default'} />
                      )}
                      {div.date && <Chip label={fmtDate(div.date)} size="small" variant="outlined" />}
                    </Box>
                  </Box>

                  {standings.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">No teams in this division.</Typography>
                    </Box>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f4f6f9' }}>
                          <TableCell sx={{ fontWeight: 'bold', width: 60 }}>Place</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Team</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Matches</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Points Played</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Earned</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {standings.map((s, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                            <TableCell>{placeBadge(s.place)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={s.place <= 3 ? 700 : 500} component="div">
                                {(() => {
                                  // If we have matched_players, render each first name as a link to athlete or user profile
                                  const players = s.matched_players || [];
                                  if (players.length === 0) {
                                    return s.team_name;
                                  }
                                  return players.map((p, i) => {
                                    const firstName = p.first_name || '';
                                    const target = p.athlete_id
                                      ? `/athletes/${p.athlete_id}`
                                      : p.user_id
                                        ? `/people/${p.user_id}`
                                        : null;
                                    const node = target ? (
                                      <Link to={target}
                                            style={{ color: '#0D1E35', textDecoration: 'underline', textDecorationColor: 'rgba(13,30,53,0.3)' }}
                                            onMouseEnter={e => e.target.style.textDecorationColor = '#C9A84C'}
                                            onMouseLeave={e => e.target.style.textDecorationColor = 'rgba(13,30,53,0.3)'}>
                                        {firstName}
                                      </Link>
                                    ) : (
                                      <span>{firstName}</span>
                                    );
                                    return (
                                      <React.Fragment key={i}>
                                        {i > 0 && <span style={{ color: '#888' }}> / </span>}
                                        {node}
                                      </React.Fragment>
                                    );
                                  });
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {s.wins} / {s.losses}
                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                  {s.win_pct.toFixed(1)}%
                                </Typography>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {s.points_for} / {s.points_against}
                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                  {s.points_pct.toFixed(1)}%
                                </Typography>
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', fontWeight: 600, color: '#C9A84C' }}>
                              {s.earned_points}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Card>
              );
            })}

            {results.imported_at && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 2, color: 'text.secondary' }}>
                Results imported {results.imported_at?.toDate ? results.imported_at.toDate().toLocaleString() : ''}
                {results.source_url && (
                  <> · <a href={results.source_url} target="_blank" rel="noopener noreferrer">source</a></>
                )}
              </Typography>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
